import { __assign, __rest } from "tslib";
import { Calendar as CalendarIcon } from '@mos-cat/icon';
import MaskedField, { VARIANT } from '@mos-cat/masked-field';
import { POSITION } from '@mos-cat/popover-wrapper';
import cn from 'classnames';
import moment from 'moment';
import * as React from 'react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Calendar from './inner/Calendar';
import './styles/Datepicker.scss';
import 'moment/locale/ru';
var MIN_TABLET_WIDTH = 768;
function useWindowWidth() {
    var _a = useState(window.innerWidth), width = _a[0], setWidth = _a[1];
    useEffect(function () {
        var listener = function () {
            setWidth(window.innerWidth);
        };
        window.addEventListener('resize', listener);
        return function () {
            window.removeEventListener('resize', listener);
        };
    });
    return width;
}
/**
 * Функция обманывает треккер реакта, делая вид, что значение ещё не менялось. Это позволяет кинуть событие `change` с элемента.
 * Нужно избавиться от этого куска, либо пробросив в onChange клик из Calendar, либо переделать на [react-events](https://github.com/facebook/react/tree/master/packages/react-events) когда он зарелизится
 * @param element
 * @param value
 */
function setNativeValue(element, value) {
    var previousValue = element.value;
    // eslint-disable-next-line no-param-reassign
    element.value = value;
    var tracker = element._valueTracker;
    if (tracker) {
        tracker.setValue(previousValue);
    }
}
function transformMobileEvent(event) {
    event.persist();
    var input = event.target.cloneNode(true);
    input.type = 'text';
    input.value = moment(input.value, 'YYYY-MM-DD').format('DD.MM.YYYY');
    event.target = input;
    return event;
}
function getAlternatingProps(isMobile, props) {
    var type = isMobile ? 'date' : 'text';
    var value = isMobile
        ? props.value && moment(props.value, 'DD.MM.YYYY').format('YYYY-MM-DD')
        : props.value;
    var maskOptions = isMobile
        ? null
        : {
            mask: '(9{1,2}.){2}9{4}',
            showMaskOnHover: false,
            jitMasking: true
        };
    var placeholder = isMobile ? undefined : 'ДД.ММ.ГГГГ';
    var onChange = function (event) {
        return props.onChange(isMobile ? transformMobileEvent(event) : event);
    };
    return { value: value, maskOptions: maskOptions, placeholder: placeholder, onChange: onChange, type: type };
}
var Datepicker = forwardRef(function Datepicker(props, ref) {
    // Хуки
    var inputRef = useRef();
    useImperativeHandle(ref, function () { return inputRef.current; });
    var _a = useState(false), popoverOpened = _a[0], setPopoverOpenedState = _a[1];
    var _b = useState(props.initialDisplayDate || ''), displayDate = _b[0], setDisplayDate = _b[1];
    var windowWidth = useWindowWidth();
    useEffect(function () {
        if (!props.value) {
            setDisplayDate(props.initialDisplayDate);
        }
    }, [props.initialDisplayDate, props.value]);
    // Пропсы
    var wrapperClassName = props.wrapperClassName, minDate = props.minDate, value = props.value, className = props.className, maxDate = props.maxDate, calendarPosition = props.calendarPosition, suffix = props.suffix, restProps = __rest(props, ["wrapperClassName", "minDate", "value", "className", "maxDate", "calendarPosition", "suffix"]);
    var isMobile = windowWidth < MIN_TABLET_WIDTH;
    var alternatingProps = getAlternatingProps(isMobile, props);
    return (React.createElement(Calendar, { opened: !isMobile && popoverOpened, position: calendarPosition || POSITION.bottom, className: wrapperClassName, displayDate: displayDate, selectedDate: value, onMonthChange: setDisplayDate, onDateSelect: function (date) {
            var input = inputRef.current;
            if (!input)
                return;
            setNativeValue(input, date);
            input.dispatchEvent(new Event('change', { bubbles: true }));
        }, minDate: minDate, maxDate: maxDate },
        React.createElement(MaskedField, __assign({}, restProps, alternatingProps, { placeholder: popoverOpened && alternatingProps.placeholder, ref: inputRef, className: cn('Datepicker', className), onFocus: function (event) {
                props.onFocus(isMobile ? transformMobileEvent(event) : event);
                if (event.defaultPrevented)
                    return;
                setPopoverOpenedState(true);
            }, onBlur: function (event) {
                props.onBlur(isMobile ? transformMobileEvent(event) : event);
                if (event.defaultPrevented)
                    return;
                setPopoverOpenedState(false);
            }, min: moment(minDate, 'DD.MM.YYYY').format('YYYY-MM-DD'), max: moment(maxDate, 'DD.MM.YYYY').format('YYYY-MM-DD'), suffix: []
                .concat(suffix)
                .concat(React.createElement(CalendarIcon, { className: 'CalendarIcon' }))
                .filter(Boolean) }))));
});
Datepicker.defaultProps = {
    onFocus: function () { },
    onBlur: function () { },
    calendarPosition: POSITION.bottom
};
/**
 * @visibleName Datepicker
 */
export default Object.assign(Datepicker, {
    POSITION: POSITION,
    VARIANT: VARIANT
});
export { POSITION, VARIANT };
//# sourceMappingURL=Datepicker.js.map